import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { CreditCard, Plus, Users as UsersIcon, X } from "lucide-react";
import { ApiError } from "@finora/api-client";
import { CompanyUserRole } from "@finora/types";
import type { UserDto } from "@finora/types";
import { Button, EmptyState, Skeleton } from "@finora/ui";
import { api } from "../../lib/api";

const ROLE_LABELS: Record<CompanyUserRole, string> = {
  OWNER: "Vlasnik",
  ADMIN: "Administrator",
  CASHIER: "Kasir",
  ACCOUNTANT: "Knjigovođa",
  VIEWER: "Samo pregled",
};

function NewUserModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState<CompanyUserRole>(CompanyUserRole.CASHIER);
  const [purchasing, setPurchasing] = useState(false);

  const save = useMutation({
    mutationFn: () => api.createUser({ email, fullName, password, role }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      queryClient.invalidateQueries({ queryKey: ["entitlements"] });
      onClose();
    },
  });

  const errorCode = save.error instanceof ApiError ? save.error.response.errorCode : null;
  const isSeatLimitReached = errorCode === "USER_SEAT_LIMIT_REACHED";
  // Kasir bez aktivnog "kasa" modula - backend baca istu grešku kao
  // ModuleAccessGuard (vidi EntitlementService.assertModuleAccess), pa se
  // nudi ista "Aktiviraj dodatak" CTA - vidi razgovor "a kasir ne moze da
  // se doda ako nema kasu".
  const isModuleMissing = errorCode === "MODULE_NOT_AVAILABLE";
  const moduleAccess = save.error instanceof ApiError ? save.error.response.moduleAccess : undefined;

  const handleBuyExtraSeat = async () => {
    setPurchasing(true);
    try {
      await api.purchaseExtraUserSeat();
      navigate("/moji-racuni");
    } finally {
      setPurchasing(false);
    }
  };

  const handleActivateKasa = async () => {
    setPurchasing(true);
    try {
      await api.purchaseAddon("kasa");
      navigate("/moji-racuni");
    } finally {
      setPurchasing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <div className="w-full max-w-md rounded-xl bg-surface shadow-xl">
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <h2 className="text-base font-semibold text-slate-900">Novi korisnik</h2>
          <button onClick={onClose} className="rounded-md p-1 text-slate-400 hover:bg-slate-100">
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>
        <div className="space-y-4 px-5 py-5">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Ime i prezime</label>
            <input
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Početna lozinka</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Bar 6 karaktera"
              className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Rola</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as CompanyUserRole)}
              className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
            >
              {Object.values(CompanyUserRole).map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABELS[r]}
                </option>
              ))}
            </select>
          </div>
          {save.isError && (isSeatLimitReached || isModuleMissing) && (
            <div className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
              {save.error instanceof ApiError ? save.error.response.message : "Dodavanje korisnika nije uspelo."}
            </div>
          )}
          {save.isError && !isSeatLimitReached && !isModuleMissing && (
            <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
              {save.error instanceof ApiError ? save.error.response.message : "Čuvanje nije uspelo."}
            </p>
          )}
        </div>
        <div className="flex gap-3 border-t border-border px-5 py-4">
          <Button variant="secondary" className="flex-1" onClick={onClose}>
            Otkaži
          </Button>
          {isSeatLimitReached ? (
            <Button className="flex-1" disabled={purchasing} onClick={handleBuyExtraSeat}>
              {purchasing ? "Kreiranje ponude…" : "Aktiviraj dodatno mesto"}
            </Button>
          ) : isModuleMissing && moduleAccess?.addonAvailable ? (
            <Button className="flex-1" disabled={purchasing} onClick={handleActivateKasa}>
              {purchasing ? "Kreiranje ponude…" : "Aktiviraj dodatak"}
            </Button>
          ) : (
            <Button
              className="flex-1"
              disabled={!email || !fullName || password.length < 6 || save.isPending}
              onClick={() => save.mutate()}
            >
              Sačuvaj
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * NFC kartica login (Finora Mobile) - dodela/uklanjanje kartice za
 * zaposlenog. Vidi "Dopuna specifikacije" 25.09.2026 - backend rute su
 * POST/DELETE /users/:id/card, vec deploy-ovane. Meko uklanjanje na
 * backend-u (zapis ostaje, samo isActive:false) - ovde se to ne vidi,
 * korisniku se prikazuje samo trenutno stanje (user.cardActive).
 */
function NfcCardSection({ user }: { user: UserDto }) {
  const queryClient = useQueryClient();
  const [cardSerial, setCardSerial] = useState("");
  const [pin, setPin] = useState("");

  const assign = useMutation({
    mutationFn: () => api.assignEmployeeCard(user.id, { cardSerial, pin }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      setCardSerial("");
      setPin("");
    },
  });

  const remove = useMutation({
    mutationFn: () => api.removeEmployeeCard(user.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });

  // 409 nosi i "assignedTo" (ime zaposlenog kod koga je kartica vec
  // AKTIVNA) - vidi UsersService.assignCard.
  const conflictAssignedTo = assign.error instanceof ApiError ? assign.error.response.assignedTo : undefined;

  return (
    <div className="rounded-lg border border-border p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="flex items-center gap-2 text-sm font-medium text-slate-700">
          <CreditCard className="h-4 w-4" aria-hidden />
          NFC prijava (HAFiskal Mobile)
        </span>
        {user.cardActive ? (
          <span className="text-xs font-medium text-emerald-600">Aktivna</span>
        ) : (
          <span className="text-xs text-slate-400">Nema karticu</span>
        )}
      </div>

      {user.cardActive ? (
        <Button
          type="button"
          variant="secondary"
          className="w-full"
          disabled={remove.isPending}
          onClick={() => remove.mutate()}
        >
          {remove.isPending ? "Isključivanje…" : "Isključi NFC prijavu"}
        </Button>
      ) : (
        <div className="space-y-2">
          <input
            value={cardSerial}
            onChange={(e) => setCardSerial(e.target.value)}
            placeholder="Serijski broj kartice (npr. 04A3B21C5D6E80)"
            className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
          />
          <input
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
            placeholder="Početni PIN (4-8 cifara)"
            maxLength={8}
            className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
          />
          <Button
            type="button"
            variant="secondary"
            className="w-full"
            disabled={!cardSerial || pin.length < 4 || assign.isPending}
            onClick={() => assign.mutate()}
          >
            {assign.isPending ? "Dodeljivanje…" : "Dodeli karticu"}
          </Button>
        </div>
      )}

      {assign.isError && (
        <p className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">
          {assign.error instanceof ApiError
            ? conflictAssignedTo
              ? `${assign.error.response.message} (dodeljena: ${conflictAssignedTo})`
              : assign.error.response.message
            : "Dodela kartice nije uspela."}
        </p>
      )}
      {remove.isError && (
        <p className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">
          {remove.error instanceof ApiError ? remove.error.response.message : "Isključivanje nije uspelo."}
        </p>
      )}
    </div>
  );
}

function EditUserModal({ user, onClose }: { user: UserDto; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [fullName, setFullName] = useState(user.fullName);
  const [role, setRole] = useState<CompanyUserRole>(user.role);
  const [isActive, setIsActive] = useState(user.isActive);
  const [newPassword, setNewPassword] = useState("");

  const save = useMutation({
    mutationFn: () =>
      api.updateUser(user.id, {
        fullName,
        role,
        isActive,
        password: newPassword || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      onClose();
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <div className="w-full max-w-md rounded-xl bg-surface shadow-xl">
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <h2 className="text-base font-semibold text-slate-900">Izmena korisnika</h2>
          <button onClick={onClose} className="rounded-md p-1 text-slate-400 hover:bg-slate-100">
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>
        <div className="space-y-4 px-5 py-5">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Ime i prezime</label>
            <input
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Email</label>
            <input
              value={user.email}
              disabled
              className="h-10 w-full rounded-lg border border-border bg-app-bg px-3 text-sm text-slate-400"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Rola</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as CompanyUserRole)}
              className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
            >
              {Object.values(CompanyUserRole).map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABELS[r]}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">
              Nova lozinka (ostavi prazno da se ne menja)
            </label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Bar 6 karaktera"
              className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="h-4 w-4 rounded border-border text-brand focus:ring-brand"
            />
            Aktivan (može da se prijavi)
          </label>
          <NfcCardSection user={user} />
          {save.isError && (
            <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
              {save.error instanceof ApiError ? save.error.response.message : "Čuvanje nije uspelo."}
            </p>
          )}
        </div>
        <div className="flex gap-3 border-t border-border px-5 py-4">
          <Button variant="secondary" className="flex-1" onClick={onClose}>
            Otkaži
          </Button>
          <Button
            className="flex-1"
            disabled={(newPassword.length > 0 && newPassword.length < 6) || save.isPending}
            onClick={() => save.mutate()}
          >
            Sačuvaj
          </Button>
        </div>
      </div>
    </div>
  );
}

export function AdminUsersPage() {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<UserDto | null>(null);
  const { data: users, isLoading } = useQuery({
    queryKey: ["users"],
    queryFn: () => api.listUsers(),
  });
  // "Korisnici isti +/- princip kao magacini" - vidi razgovor.
  const { data: entitlements } = useQuery({
    queryKey: ["entitlements"],
    queryFn: () => api.getEntitlements(),
    staleTime: 60_000,
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Korisnici</h1>
          <p className="text-sm text-slate-500">
            Upravljanje nalozima za pristup HAFiskal aplikaciji
            {entitlements?.users && (
              <span>
                {" "}
                — {entitlements.users.used} / {entitlements.users.included} uključeno
                {Number(entitlements.users.additionalPrice) > 0 && (
                  <span className="text-slate-400">
                    {" "}
                    (dodatno {entitlements.users.additionalPriceGross} RSD/mesečno sa PDV-om)
                  </span>
                )}
              </span>
            )}
          </p>
        </div>
        <Button onClick={() => setModalOpen(true)}>
          <Plus className="h-4 w-4" aria-hidden />
          Novi korisnik
        </Button>
      </div>

      <div className="rounded-xl border border-border bg-surface shadow-subtle">
        {isLoading ? (
          <div className="space-y-2 p-5">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : !users || users.length === 0 ? (
          <div className="p-5">
            <EmptyState icon={UsersIcon} title="Nema korisnika" description="Dodajte prvog korisnika dugmetom iznad." />
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Ime i prezime</th>
                <th>Email</th>
                <th>Rola</th>
                <th>Status</th>
                <th>NFC kartica</th>
                <th>Poslednja prijava</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setEditingUser(u)}>
                  <td className="font-medium text-slate-800">{u.fullName}</td>
                  <td className="text-slate-500">{u.email}</td>
                  <td className="text-slate-500">{ROLE_LABELS[u.role]}</td>
                  <td>
                    {u.isActive ? (
                      <span className="text-xs text-emerald-600">Aktivan</span>
                    ) : (
                      <span className="text-xs text-red-500">Deaktiviran</span>
                    )}
                  </td>
                  <td>
                    {u.cardActive ? (
                      <span className="text-xs font-medium text-emerald-600">Aktivna</span>
                    ) : (
                      <span className="text-xs text-slate-400">—</span>
                    )}
                  </td>
                  <td className="text-slate-500">
                    {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString("sr-RS") : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modalOpen && <NewUserModal onClose={() => setModalOpen(false)} />}
      {editingUser && <EditUserModal user={editingUser} onClose={() => setEditingUser(null)} />}
    </div>
  );
}
